import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { EmptyStateComponent } from '../../../../../components/empty-state/empty-state.component';
import { SkeletonComponent } from '../../../../../components/skeleton/skeleton.component';
import {
  ITemplateFieldModalResult,
  TemplateFieldModalComponent,
} from '../../../../../components/template-field-modal/template-field-modal.component';
import {
  ITemplateModalResult,
  TemplateModalComponent,
} from '../../../../../components/template-modal/template-modal.component';
import { FieldTypeModel } from '../../../../../models/field-type.model';
import { TemplateFieldModel } from '../../../../../models/template-field.model';
import { DateToMomentPipe } from '../../../../../pipes/date-to-moment.pipe';
import { ConfirmService } from '../../../../../services/confirm.service';
import { FieldTypeService } from '../../../../../services/field-type.service';
import { SpinnerService } from '../../../../../services/spinner.service';
import {
  ITemplateFieldCreateRequest,
  ITemplateFieldUpdateRequest,
  TemplateService,
} from '../../../../../services/template.service';
import { ToastService } from '../../../../../services/toast.service';

/**
 * Como va un campo respecto al servidor. Sin entrada en `fieldRowStates` =
 * en reposo (sincronizado, o nunca tocado). Con entrada = hay una peticion en
 * vuelo o quedo pendiente de resolver el usuario.
 */
type FieldSyncStatus = 'creating' | 'updating' | 'deleting' | 'error';

interface IFieldRowState {
  status: FieldSyncStatus;
  /** Mensaje de error legible. Solo se usa cuando `status === 'error'`. */
  message?: string;
  /**
   * Body ORIGINAL del alta que fallo, para poder reintentar sin perder lo que
   * el usuario capturo ni reabrir el modal. Solo las ALTAS terminan en
   * `'error'`: una edicion que falla se REVIERTE al valor anterior en vez de
   * quedarse pendiente (ver `persistFieldUpdate`), asi que nunca necesita
   * esto.
   */
  retryRequest?: ITemplateFieldCreateRequest;
}

/** Prefijo de los ids temporales de una alta optimista. Ver `createTempId`. */
const TEMP_ID_PREFIX = 'tmp-';

/**
 * Detalle de una plantilla: CRUD de sus campos.
 *
 * Los campos NO tienen GET propio: llegan dentro de `template.fields`. El orden
 * se edita en local (arrastrando o con las flechas) y se persiste al confirmar,
 * con un PUT por campo movido; asi el arrastre no dispara una tormenta de
 * peticiones.
 *
 * ALTA Y EDICION DE CAMPOS: patron OPTIMISTA, no bloqueante. El modal cierra
 * de inmediato y la fila aparece (o cambia) en la tabla al instante, marcada
 * como "en curso"; el POST/PUT real corre en segundo plano sin el overlay
 * global de `SpinnerService` (reservado a mutaciones que de verdad deben
 * impedir seguir usando la pantalla). Ver `IFieldRowState` para el modelo de
 * estados y los metodos `add/replace/removeFieldLocally` de `TemplateService`
 * para como se mantienen sincronizadas `orderedFields` (la copia de trabajo
 * de esta pagina) y `template().fields` (la fuente compartida que lee
 * `orderChanged()`).
 *
 * BORRADO (desactivar): NO es optimista a proposito. La fila se deshabilita
 * y muestra progreso, pero se queda en la tabla hasta que el servidor
 * confirma; solo entonces se refleja el `status = false` que devuelve. Es un
 * soft-delete (el API no expone borrado fisico de campos), asi que la fila
 * nunca desaparece de la lista, ni antes ni despues.
 *
 * Los PRERELLENADOS no tienen panel aqui: un prerellenado («Netflix») pertenece
 * a la plantilla, pero cada uno de sus valores pertenece a UN campo. Se
 * administran en Catalogos → Prerellenados, su propia seccion.
 */
@Component({
  selector: 'app-template-page',
  imports: [DateToMomentPipe, EmptyStateComponent, RouterLink, SkeletonComponent],
  templateUrl: './template-page.component.html',
  styleUrl: './template-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TemplatePageComponent {
  private readonly templateService = inject(TemplateService);
  private readonly fieldTypeService = inject(FieldTypeService);
  private readonly modalService = inject(NgbModal);
  private readonly confirmService = inject(ConfirmService);
  private readonly spinnerService = inject(SpinnerService);
  private readonly toastService = inject(ToastService);

  /** Llega del router gracias a `withComponentInputBinding()`. */
  readonly templateId = input.required<string>();

  protected readonly template = this.templateService.template;

  /**
   * `true` cuando la PRIMERA carga ya termino, con exito o con error. Es lo que
   * decide si se pueden pintar estados vacios: `loading` no sirve para eso
   * porque arranca en `false` y solo se enciende cuando arranca la peticion, de
   * modo que el primer frame pintaria «Sin campos» y se veria un parpadeo.
   *
   * Vive en el componente, no en el servicio (que es un singleton): asi vuelve
   * a `false` al abrir otra plantilla. Y NO se reinicia en las recargas, para
   * que editar un campo no haga desaparecer la ficha entera.
   */
  protected readonly loaded = signal(false);

  /**
   * `true` mientras se releen los campos sobre una ficha ya pintada. Solo el
   * panel de campos vuelve a esqueletos; el resto de la ficha se queda.
   */
  protected readonly reloadingFields = signal(false);

  /**
   * Mensaje del fallo de carga, o `null`. Distingue «no se pudo cargar» (fallo
   * del API) de «la plantilla no existe» y de «no tiene campos»: son tres cosas
   * distintas y el usuario necesita saber cual es.
   */
  protected readonly templateError = this.templateService.templateError;

  /**
   * Catalogo COMPLETO de tipos de campo. El modal filtra los activos para su
   * select, pero necesita los inactivos para poder resolver el tipo de un campo
   * cuyo tipo ya se apago (y saber si su valor va enmascarado).
   */
  protected readonly fieldTypes = signal<FieldTypeModel[]>([]);

  /** Orden de trabajo: se reordena en local y se guarda al confirmar. */
  protected readonly orderedFields = signal<TemplateFieldModel[]>([]);

  /** Indice que se esta arrastrando, o `null`. */
  protected readonly draggingIndex = signal<number | null>(null);

  /**
   * Estado de sincronizacion por campo, indexado por `templateFieldId` (el
   * temporal mientras esta en alta, el real en cuanto se conoce). Sin entrada
   * para un id = esa fila esta en reposo. `Record` en vez de `Map` porque los
   * signals comparan por referencia: reemplazar el objeto entero en cada
   * cambio es lo que dispara a `hasBlockingFields` y a la plantilla.
   */
  protected readonly fieldRowStates = signal<Record<string, IFieldRowState>>({});

  /**
   * Texto que se anuncia a lectores de pantalla al iniciar cada transicion de
   * estado (alta, edicion, borrado, reintento, descarte). A proposito NO
   * repite el resultado final: eso ya lo anuncia el `aria-live` propio de
   * `<app-toasts>` con el mensaje de exito o error del servidor. Duplicarlo
   * aqui seria "inundar" al lector de pantalla con el mismo aviso dos veces.
   */
  protected readonly liveMessage = signal('');

  /** True si el orden en pantalla ya no coincide con el guardado. */
  protected readonly orderChanged = computed(() => {
    const current = this.orderedFields();
    const saved = this.sortFields(this.template()?.fields ?? []);

    if (current.length !== saved.length) return true;

    return current.some((field, index) => field.templateFieldId !== saved[index].templateFieldId);
  });

  protected readonly activeFieldCount = computed(
    () => this.orderedFields().filter((field) => field.status).length,
  );

  /**
   * Hay al menos un campo con una peticion en vuelo o en error. Mientras sea
   * `true`: ningun campo bloqueado se puede arrastrar y "Guardar orden" se
   * deshabilita, porque una fila pendiente aun no tiene `templateFieldId`
   * real (o esta a medio actualizar) y mandarla en el PUT de reordenamiento
   * rompería.
   */
  protected readonly hasBlockingFields = computed(
    () => Object.keys(this.fieldRowStates()).length > 0,
  );

  constructor() {
    effect(() => {
      const templateId = this.templateId();
      void this.load(templateId);
    });

    // Ninguna escritura sobre signals de ESTE componente despues de destruido:
    // una peticion de campo puede seguir en vuelo si el usuario navega fuera
    // a media alta/edicion/borrado. Las funciones `persist*` de mas abajo
    // comprueban esta bandera antes de tocar `orderedFields`/`fieldRowStates`.
    inject(DestroyRef).onDestroy(() => {
      this.destroyed = true;
    });
  }

  private destroyed = false;

  /** Ultima plantilla pedida: distingue «otra plantilla» de «recarga». */
  private lastRequestedId: string | null = null;

  private sortFields(fields: TemplateFieldModel[]): TemplateFieldModel[] {
    return [...fields].sort((a, b) => a.position - b.position || a.label.localeCompare(b.label));
  }

  private async load(templateId: string): Promise<void> {
    // Si se abre OTRA plantilla, lo que hay en pantalla ya no es de esta ficha:
    // se vuelve a esqueletos. Una recarga de la MISMA plantilla no los pinta,
    // para que editar un campo no haga desaparecer la ficha entera.
    if (this.lastRequestedId !== templateId) {
      this.lastRequestedId = templateId;
      this.loaded.set(false);
    }

    this.reloadingFields.set(true);
    // Cualquier alta/edicion/borrado pendiente de ESTA vista pertenece a los
    // datos que se estan por reemplazar: sin esto, una recarga completa (la
    // dispara solo desactivar/activar la PLANTILLA, no los campos) podria
    // dejar `hasBlockingFields()` encendido para siempre con un id que ya no
    // existe en la lista fresca.
    this.fieldRowStates.set({});

    try {
      const template = await this.templateService.getTemplate(templateId);
      this.orderedFields.set(this.sortFields(template?.fields ?? []));

      this.fieldTypes.set(await this.fieldTypeService.getFieldTypes());
    } finally {
      // Tambien tras un fallo: lo que apaga los esqueletos es «ya se intento»,
      // no «salio bien». El error se cuenta aparte, con `templateError()`.
      this.reloadingFields.set(false);
      this.loaded.set(true);
    }
  }

  protected async reload(): Promise<void> {
    await this.load(this.templateId());
  }

  // ---------------------------------------------------------------------------
  // Plantilla
  // ---------------------------------------------------------------------------

  protected async edit(): Promise<void> {
    const template = this.template();
    if (!template) return;

    const reference = this.modalService.open(TemplateModalComponent, {
      centered: true,
      backdrop: 'static',
      scrollable: true,
      ariaLabelledBy: 'ks-template-modal-title',
    });

    (reference.componentInstance as TemplateModalComponent).init(template);

    let result: ITemplateModalResult;

    try {
      result = (await reference.result) as ITemplateModalResult;
    } catch {
      return;
    }

    // `showSpinner: false`: el dueño pidio poder seguir dando de alta o
    // editando campos MIENTRAS se guarda la plantilla. El overlay global lo
    // impedia (bloqueaba toda la pantalla, no solo esta cabecera). El toast
    // de exito/error lo sigue mostrando el servicio igual que siempre.
    const updated = await this.templateService.updateTemplate(
      template.templateId,
      {
        name: result.name,
        description: result.description,
        generateQr: result.generateQr,
        status: result.status,
        contentType: result.contentType,
      },
      false,
    );

    if (!updated) return;

    if (result.file && updated.presignFile) {
      await this.templateService.uploadFile(updated.presignFile, result.file);
    }

    await this.reload();
  }

  /** Borrado SOFT: la plantilla se desactiva, no desaparece. */
  protected async deactivate(): Promise<void> {
    const template = this.template();
    if (!template) return;

    const confirmed = await this.confirmService.askDeactivate(
      'la plantilla',
      `${template.name} · ${template.code}`,
    );

    if (!confirmed) return;

    const deleted = await this.templateService.deleteTemplate(template.templateId);
    if (deleted) await this.reload();
  }

  /** Reactivar es un PUT con `status = true`. */
  protected async activate(): Promise<void> {
    const template = this.template();
    if (!template) return;

    const updated = await this.templateService.updateTemplate(template.templateId, {
      name: template.name,
      description: template.description,
      generateQr: template.generateQr,
      status: true,
      contentType: null,
    });

    if (updated) await this.reload();
  }

  // ---------------------------------------------------------------------------
  // Campos — alta y edicion OPTIMISTAS
  // ---------------------------------------------------------------------------

  private createTempId(): string {
    return `${TEMP_ID_PREFIX}${crypto.randomUUID()}`;
  }

  /** Ids reales del API son numericos (BIGINT); el prefijo no puede colisionar. */
  private isTempId(templateFieldId: string): boolean {
    return templateFieldId.startsWith(TEMP_ID_PREFIX);
  }

  protected rowState(field: TemplateFieldModel): IFieldRowState | undefined {
    return this.fieldRowStates()[field.templateFieldId];
  }

  protected isRowBlocked(field: TemplateFieldModel): boolean {
    return this.rowState(field) !== undefined;
  }

  private setRowState(templateFieldId: string, state: IFieldRowState): void {
    if (this.destroyed) return;
    this.fieldRowStates.update((states) => ({ ...states, [templateFieldId]: state }));
  }

  private clearRowState(templateFieldId: string): void {
    if (this.destroyed) return;
    this.fieldRowStates.update((states) => {
      if (!(templateFieldId in states)) return states;
      const next = { ...states };
      delete next[templateFieldId];
      return next;
    });
  }

  private announce(message: string): void {
    if (this.destroyed) return;
    this.liveMessage.set(message);
  }

  protected rowStatusText(status: FieldSyncStatus): string {
    switch (status) {
      case 'creating':
        return 'Guardando…';
      case 'updating':
        return 'Guardando cambios…';
      case 'deleting':
        return 'Desactivando…';
      case 'error':
        return 'No se pudo guardar';
    }
  }

  protected rowStatusIcon(status: FieldSyncStatus): string {
    return status === 'error' ? 'bi-exclamation-triangle' : 'bi-arrow-repeat';
  }

  protected rowActionLabel(base: string, field: TemplateFieldModel): string {
    return this.isRowBlocked(field) ? `${base} (procesando, espera)` : base;
  }

  protected async createField(): Promise<void> {
    const template = this.template();
    if (!template) return;

    const result = await this.openFieldModal(null);
    if (!result) return;

    // Se agrega al final del orden actual. La fila optimista que se inserta a
    // continuacion YA cuenta en `orderedFields().length`, asi que un segundo
    // alta inmediatamente despues calcula bien la siguiente posicion.
    const request: ITemplateFieldCreateRequest = {
      fieldType: result.fieldType,
      label: result.label,
      position: this.orderedFields().length,
      required: result.required,
      primaryField: result.primaryField,
    };

    this.insertOptimisticField(template.templateId, request);
  }

  /** Inserta la fila optimista y lanza el POST en segundo plano. No bloquea. */
  private insertOptimisticField(templateId: string, request: ITemplateFieldCreateRequest): void {
    const tempId = this.createTempId();
    const nowIso = new Date().toISOString();

    const optimisticField = new TemplateFieldModel({
      templateFieldId: tempId,
      templateId,
      fieldType: request.fieldType,
      label: request.label,
      position: request.position,
      required: request.required,
      primaryField: request.primaryField,
      status: true,
      uidCreatedBy: null,
      uidUpdatedBy: null,
      createdAt: nowIso,
      updatedAt: nowIso,
    });

    this.orderedFields.update((fields) => [...fields, optimisticField]);
    this.templateService.addFieldLocally(optimisticField);
    this.setRowState(tempId, { status: 'creating', retryRequest: request });
    this.announce(`Guardando el campo ${request.label}…`);

    void this.persistNewField(templateId, tempId, request);
  }

  private async persistNewField(
    templateId: string,
    tempId: string,
    request: ITemplateFieldCreateRequest,
  ): Promise<void> {
    const created = await this.templateService.createTemplateField(templateId, request);

    if (this.destroyed) return;

    if (!created) {
      this.setRowState(tempId, {
        status: 'error',
        message: 'No se pudo guardar el campo. Reintenta o descártalo.',
        retryRequest: request,
      });
      this.announce(`No se pudo guardar el campo ${request.label}. Puedes reintentar o descartarlo.`);
      return;
    }

    // Reconciliacion: el id temporal se sustituye por el real EN EL MISMO
    // LUGAR de las dos listas (la de trabajo y la del servicio), asi que
    // `orderChanged()` se queda en `false` si el usuario no movio nada.
    this.orderedFields.update((fields) =>
      fields.map((field) => (field.templateFieldId === tempId ? created : field)),
    );
    this.templateService.replaceFieldLocally(tempId, created);

    // El API ya apago el `primaryField` del hermano que lo tenia (transaccion
    // atomica), pero esta copia en LOCAL de los campos no se entera sola: hay
    // que reflejarlo aqui para que la tabla no muestre dos campos "Principal"
    // hasta la proxima recarga.
    if (created.primaryField) this.demoteSiblingPrimaryFields(created.templateFieldId);

    this.clearRowState(tempId);
    this.announce(`Campo ${created.label} guardado.`);
  }

  /** Reenvia el MISMO body que fallo: nada de lo que el usuario capturo se pierde. */
  protected retryCreateField(field: TemplateFieldModel): void {
    const template = this.template();
    const state = this.rowState(field);
    if (!template || !state?.retryRequest) return;

    this.setRowState(field.templateFieldId, { status: 'creating', retryRequest: state.retryRequest });
    this.announce(`Reintentando guardar el campo ${field.label}…`);
    void this.persistNewField(template.templateId, field.templateFieldId, state.retryRequest);
  }

  /** Descarta una alta que fallo: nunca llego a existir en el servidor. */
  protected discardField(field: TemplateFieldModel): void {
    this.orderedFields.update((fields) =>
      fields.filter((item) => item.templateFieldId !== field.templateFieldId),
    );
    this.templateService.removeFieldLocally(field.templateFieldId);
    this.clearRowState(field.templateFieldId);
    this.announce(`Se descartó el campo ${field.label}.`);
  }

  protected async editField(field: TemplateFieldModel): Promise<void> {
    const template = this.template();
    if (!template || this.isRowBlocked(field)) return;

    const result = await this.openFieldModal(field);
    if (!result) return;

    const request: ITemplateFieldUpdateRequest = {
      label: result.label,
      position: field.position,
      required: result.required,
      primaryField: result.primaryField,
      status: result.status,
    };

    this.applyOptimisticFieldUpdate(template.templateId, field, request);
  }

  /** Aplica el cambio al instante (marcado "guardando") y lanza el PUT detras. */
  private applyOptimisticFieldUpdate(
    templateId: string,
    previous: TemplateFieldModel,
    request: ITemplateFieldUpdateRequest,
  ): void {
    const optimistic = new TemplateFieldModel({
      templateFieldId: previous.templateFieldId,
      templateId,
      fieldType: previous.fieldType,
      label: request.label,
      position: request.position,
      required: request.required,
      primaryField: request.primaryField,
      status: request.status,
      uidCreatedBy: previous.uidCreatedBy,
      uidUpdatedBy: previous.uidUpdatedBy,
      createdAt: previous.createdAt.toISOString(),
      updatedAt: new Date().toISOString(),
    });

    this.orderedFields.update((fields) =>
      fields.map((item) => (item.templateFieldId === previous.templateFieldId ? optimistic : item)),
    );
    this.templateService.replaceFieldLocally(previous.templateFieldId, optimistic);
    this.setRowState(previous.templateFieldId, { status: 'updating' });
    this.announce(`Guardando cambios del campo ${optimistic.label}…`);

    void this.persistFieldUpdate(templateId, previous, request);
  }

  private async persistFieldUpdate(
    templateId: string,
    previous: TemplateFieldModel,
    request: ITemplateFieldUpdateRequest,
  ): Promise<void> {
    const updated = await this.templateService.updateTemplateField(
      templateId,
      previous.templateFieldId,
      request,
    );

    if (this.destroyed) return;

    if (!updated) {
      // Revierte al valor anterior: la edicion optimista NO se queda en
      // error como el alta, porque el campo ya existia y su valor previo
      // sigue siendo valido. El toast del servicio ya explica el fallo.
      this.orderedFields.update((fields) =>
        fields.map((item) => (item.templateFieldId === previous.templateFieldId ? previous : item)),
      );
      this.templateService.replaceFieldLocally(previous.templateFieldId, previous);
      this.clearRowState(previous.templateFieldId);
      this.announce(`Se restauró el valor anterior del campo ${previous.label}.`);
      return;
    }

    this.orderedFields.update((fields) =>
      fields.map((item) => (item.templateFieldId === previous.templateFieldId ? updated : item)),
    );
    this.templateService.replaceFieldLocally(previous.templateFieldId, updated);

    // Mismo motivo que en `persistNewField`: el API ya apago el hermano en el
    // servidor, pero hay que reflejarlo en la copia local para no mostrar dos
    // campos "Principal" hasta la proxima recarga.
    if (updated.primaryField) this.demoteSiblingPrimaryFields(updated.templateFieldId);

    this.clearRowState(previous.templateFieldId);
    this.announce(`Campo ${updated.label} actualizado.`);
  }

  /**
   * Apaga en LOCAL el `primaryField` de cualquier campo que no sea `keepId`.
   * Solo se llama tras confirmar el exito de un alta/edicion que SI marco un
   * nuevo campo principal: el API ya lo hizo en el servidor (transaccion
   * atomica), esto solo evita que la tabla muestre dos campos "Principal" a
   * la vez mientras no se recarga la ficha completa.
   */
  private demoteSiblingPrimaryFields(keepId: string): void {
    const previousPrimary = this.orderedFields().find(
      (field) => field.templateFieldId !== keepId && field.primaryField,
    );
    if (!previousPrimary) return;

    const demoted = new TemplateFieldModel({
      templateFieldId: previousPrimary.templateFieldId,
      templateId: previousPrimary.templateId,
      fieldType: previousPrimary.fieldType,
      label: previousPrimary.label,
      position: previousPrimary.position,
      required: previousPrimary.required,
      primaryField: false,
      status: previousPrimary.status,
      uidCreatedBy: previousPrimary.uidCreatedBy,
      uidUpdatedBy: previousPrimary.uidUpdatedBy,
      createdAt: previousPrimary.createdAt.toISOString(),
      updatedAt: previousPrimary.updatedAt.toISOString(),
    });

    this.orderedFields.update((fields) =>
      fields.map((item) => (item.templateFieldId === previousPrimary.templateFieldId ? demoted : item)),
    );
    this.templateService.replaceFieldLocally(previousPrimary.templateFieldId, demoted);
  }

  // ---------------------------------------------------------------------------
  // Campos — borrado NO optimista
  // ---------------------------------------------------------------------------

  /**
   * Borrado SOFT del campo. A proposito NO es optimista: la fila se
   * deshabilita y muestra progreso, pero se queda en la tabla hasta que el
   * servidor confirma el `status = false`. El API no expone borrado fisico de
   * campos de plantilla, asi que esta fila jamas desaparece de la lista.
   */
  protected async deactivateField(field: TemplateFieldModel): Promise<void> {
    const template = this.template();
    if (!template || this.isRowBlocked(field)) return;

    const confirmed = await this.confirmService.askDeactivate(
      'el campo de plantilla',
      `${field.label} · ${field.fieldType}`,
    );

    if (!confirmed) return;

    this.setRowState(field.templateFieldId, { status: 'deleting' });
    this.announce(`Desactivando el campo ${field.label}…`);

    const deleted = await this.templateService.deleteTemplateField(
      template.templateId,
      field.templateFieldId,
    );

    if (this.destroyed) return;
    this.clearRowState(field.templateFieldId);

    if (!deleted) return; // Nada que revertir: nunca se aplico el cambio en local.

    this.orderedFields.update((fields) =>
      fields.map((item) => (item.templateFieldId === field.templateFieldId ? deleted : item)),
    );
    this.templateService.replaceFieldLocally(field.templateFieldId, deleted);
  }

  /**
   * Reactivar, igual que desactivar, espera la confirmacion del servidor
   * antes de reflejar el cambio: es una accion de un solo clic (sin modal),
   * asi que no hay nada capturado por el usuario que valga la pena optimizar
   * mostrando de inmediato.
   */
  protected async activateField(field: TemplateFieldModel): Promise<void> {
    const template = this.template();
    if (!template || this.isRowBlocked(field)) return;

    this.setRowState(field.templateFieldId, { status: 'updating' });
    this.announce(`Activando el campo ${field.label}…`);

    const updated = await this.templateService.updateTemplateField(
      template.templateId,
      field.templateFieldId,
      {
        label: field.label,
        position: field.position,
        required: field.required,
        primaryField: field.primaryField,
        status: true,
      },
    );

    if (this.destroyed) return;
    this.clearRowState(field.templateFieldId);

    if (!updated) return;

    this.orderedFields.update((fields) =>
      fields.map((item) => (item.templateFieldId === field.templateFieldId ? updated : item)),
    );
    this.templateService.replaceFieldLocally(field.templateFieldId, updated);
  }

  private async openFieldModal(
    field: TemplateFieldModel | null,
  ): Promise<ITemplateFieldModalResult | null> {
    const reference = this.modalService.open(TemplateFieldModalComponent, {
      centered: true,
      backdrop: 'static',
      ariaLabelledBy: 'ks-template-field-modal-title',
    });

    (reference.componentInstance as TemplateFieldModalComponent).init(
      field,
      this.fieldTypes(),
      this.orderedFields(),
    );

    try {
      return (await reference.result) as ITemplateFieldModalResult;
    } catch {
      return null;
    }
  }

  // ---------------------------------------------------------------------------
  // Reordenamiento
  // ---------------------------------------------------------------------------

  /** Mueve un campo dentro del orden local. No toca el API. */
  protected move(from: number, to: number): void {
    const fields = [...this.orderedFields()];

    if (from === to || from < 0 || to < 0 || from >= fields.length || to >= fields.length) return;

    const [moved] = fields.splice(from, 1);
    fields.splice(to, 0, moved);
    this.orderedFields.set(fields);
  }

  protected onDragStart(index: number, field: TemplateFieldModel): void {
    // Defensa extra: la fila ya pierde el atributo `draggable` en la
    // plantilla mientras esta bloqueada, pero un dragstart disparado por otra
    // via (p. ej. arrastrar el texto seleccionado) no debe moverla.
    if (this.isRowBlocked(field)) return;
    this.draggingIndex.set(index);
  }

  protected onDragOver(event: DragEvent): void {
    // Sin esto el navegador no considera la fila una zona de suelta valida.
    event.preventDefault();
  }

  protected onDrop(event: DragEvent, index: number): void {
    event.preventDefault();
    const from = this.draggingIndex();
    if (from === null) return;
    this.move(from, index);
    this.draggingIndex.set(null);
  }

  protected onDragEnd(): void {
    this.draggingIndex.set(null);
  }

  protected cancelOrder(): void {
    this.orderedFields.set(this.sortFields(this.template()?.fields ?? []));
  }

  /** Persiste el orden con un PUT por campo movido y un solo aviso al final. */
  protected async saveOrder(): Promise<void> {
    const template = this.template();
    if (!template || this.hasBlockingFields()) return;

    const fields = this.orderedFields();
    const changed = fields
      .map((field, index) => ({ field, index }))
      // El boton ya esta deshabilitado mientras `hasBlockingFields()` es
      // `true`, pero esta linea es la garantia real: un id temporal (alta
      // optimista aun sin resolver) JAMAS puede viajar en un PUT, porque el
      // servidor no lo conoce.
      .filter(({ field }) => !this.isTempId(field.templateFieldId))
      .filter(({ field, index }) => field.position !== index);

    if (changed.length === 0) return;

    this.spinnerService.show('Guardando el orden de los campos...');
    let failed = 0;

    try {
      for (const { field, index } of changed) {
        const updated = await this.templateService.updateTemplateField(
          template.templateId,
          field.templateFieldId,
          {
            label: field.label,
            position: index,
            required: field.required,
            primaryField: field.primaryField,
            status: field.status,
          },
          true,
        );

        if (!updated) failed += 1;
      }
    } finally {
      this.spinnerService.hide();
    }

    if (failed > 0) {
      this.toastService.error(
        `No se pudo guardar el orden de ${failed} de ${changed.length} campos.`,
      );
    } else {
      this.toastService.success('Orden de los campos actualizado.');
    }

    await this.reload();
  }
}
